'use client'
import { useEffect, useRef } from 'react'

export type ServiceMapPin = {
  slug: string
  name: string
  lat: number
  lng: number
  categorySlug: string
  categoryIcon: string
  categoryColor: string
}

const GORIS_LAT = 39.5127
const GORIS_LNG = 46.3543

function buildIcon(L: any, pin: ServiceMapPin, selected: boolean) {
  const size = selected ? 38 : 30
  return L.divIcon({
    className: '',
    html: `<div style="
      width:${size}px;height:${size}px;
      display:flex;align-items:center;justify-content:center;
      border-radius:50%;
      background:${pin.categoryColor};
      border:${selected ? 3 : 2}px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,.4)${selected ? `, 0 0 0 4px ${pin.categoryColor}55` : ''};
      font-size:${selected ? 18 : 14}px;
      line-height:1;
    ">${pin.categoryIcon}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  })
}

export function ServicesMap({
  pins,
  selectedSlug,
  onMarkerClick,
}: {
  pins: ServiceMapPin[]
  selectedSlug: string | null
  onMarkerClick: (slug: string) => void
}) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstance = useRef<any>(null)
  const layerGroup = useRef<any>(null)
  const hasFitBounds = useRef(false)
  const leafletRef = useRef<any>(null)
  const onMarkerClickRef = useRef(onMarkerClick)
  onMarkerClickRef.current = onMarkerClick

  // Create the map once.
  useEffect(() => {
    const el = mapRef.current
    if (!el || mapInstance.current || el.classList.contains('leaflet-container')) return

    import('leaflet').then(L => {
      if (!mapRef.current || mapRef.current.classList.contains('leaflet-container')) return
      leafletRef.current = L

      delete (L.Icon.Default.prototype as any)._getIconUrl

      const map = L.map(mapRef.current!, { zoomControl: true, scrollWheelZoom: true })
      mapInstance.current = map

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map)

      map.setView([GORIS_LAT, GORIS_LNG], 13)
      layerGroup.current = L.layerGroup().addTo(map)

      // Trigger the pins effect now that the map exists.
      renderMarkers(pins, selectedSlug)
    })

    return () => {
      if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function renderMarkers(currentPins: ServiceMapPin[], selected: string | null) {
    const L = leafletRef.current
    const map = mapInstance.current
    if (!L || !map || !layerGroup.current) return

    layerGroup.current.clearLayers()

    const withCoords = currentPins.filter(p => p.lat != null && p.lng != null)
    let selectedMarker: any = null

    withCoords.forEach(pin => {
      const isSelected = pin.slug === selected
      const marker = L.marker([pin.lat, pin.lng], { icon: buildIcon(L, pin, isSelected) })
      marker.bindPopup(`
        <div style="font-family:'Hanken Grotesk',sans-serif;min-width:150px">
          <div style="font-family:'Spectral',serif;font-size:15px;color:#1E1A16;margin-bottom:6px">${pin.name}</div>
          <a href="/services/${pin.slug}" style="font-size:12px;color:#2E3A21;border-bottom:1px solid #2E3A21;text-decoration:none">View details →</a>
        </div>
      `, { maxWidth: 220 })
      marker.on('click', () => onMarkerClickRef.current(pin.slug))
      marker.addTo(layerGroup.current)
      if (isSelected) selectedMarker = marker
    })

    if (!hasFitBounds.current && withCoords.length > 0) {
      hasFitBounds.current = true
      if (withCoords.length > 1) {
        const group = L.featureGroup(layerGroup.current.getLayers())
        map.fitBounds(group.getBounds().pad(0.15))
      } else {
        map.setView([withCoords[0].lat, withCoords[0].lng], 15)
      }
    } else if (selectedMarker) {
      map.panTo(selectedMarker.getLatLng())
      selectedMarker.openPopup()
    }
  }

  // Rebuild markers whenever the filtered pin set or selection changes.
  useEffect(() => {
    renderMarkers(pins, selectedSlug)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins, selectedSlug])

  return (
    <>
      <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
    </>
  )
}
